import {
  analytics,
  getEventTrackEnabled,
  JITSU_LOG_TAG,
  getKioskSettings,
  getFormattedItem,
  getOrderTime,
  formatMenuProductToAnalyticsProduct,
  formatProductsCart,
  formatProduct,
  saveActions,
  itemMarketingData,
  resetActions,
  sortAndHandleDuplicates,
} from '../utilities';

const getBaseURLWithHome = () => {
  return window.location.origin ? `${window.location.origin}/home` : 'https://localhost:4000/home';
};

const trackEvent = (eventName, eventProperties) => {
  if (!getEventTrackEnabled()) return;
  try {
    analytics.track(eventName, eventProperties);
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - ${eventName} error: ${error.message}`);
  }
};

const setDineMode = async (dine_mode, settings, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Dine Mode Selected event`);

    if (process.env.POI_APP_IS_NEW_GA4_ENABLED) {
      const kioskSettings = getKioskSettings(settings);

      trackEvent(tagName, {
        ...kioskSettings,
        disposition: dine_mode,
      });
    } else {
      trackEvent(tagName, {
        dine_mode,
        context: {
          page: {
            path: '/home',
            url: getBaseURLWithHome(),
          },
        },
      });
    }
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Dine Mode Selected event: ${error.message}`);
  }
};

const setPage = async page_info => {
  trackEvent('page', { page_info });
};

const setProductRemoved = async (
  product,
  quantity,
  settings,
  orderTotal,
  couponNumber,
  addedUpsells,
  tagName,
) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Product Removed event`);
    const { kioskId, currency, storeName } = getKioskSettings(settings);
    const mainProduct = addedUpsells?.find(upsell => upsell.productId === product.productId);

    const formattedItem = getFormattedItem({
      product,
      currency,
      quantity,
      couponNumber,
      itemCategory: product.parentCategory,
      main: mainProduct?.main,
    });

    trackEvent(tagName, {
      storeName,
      kioskID: kioskId,
      value: orderTotal,
      currency,
      items: [{ ...formattedItem }],
    });
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Product Removed event: ${error.message}`);
  }
};

const setProductAddedToCart = async (
  product,
  settings,
  couponNumber = '',
  itemCategory = '',
  tagName,
) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Product Added event`);

    if (process.env.POI_APP_IS_NEW_GA4_ENABLED) {
      const { currency, kioskId, storeName } = getKioskSettings(settings);
      const formattedProduct = getFormattedItem({
        product,
        currency,
        couponNumber,
        itemCategory,
        quantity: product?.count?.current || 1,
        isCrossSell: product.isCrossSell,
      });

      trackEvent(tagName, {
        storeName,
        kioskID: kioskId,
        currency,
        value: product?.price?.current,
        coupon: couponNumber,
        items: [{ ...formattedProduct }],
      });
    } else {
      if (product?.type === 'grouped') return;
      const formattedProduct = formatProduct(product);

      trackEvent(tagName, {
        ...formattedProduct,
        quantity: product?.count?.current,
      });
    }
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Product Added event: ${error.message}`);
  }
};

const setUpsellAddedToCart = (product, settings, couponNumber, main, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Upsell Added event`);

    if (process.env.POI_APP_IS_NEW_GA4_ENABLED) {
      const { isCrossSell } = product;
      const { currency, kioskId, storeName } = getKioskSettings(settings);
      const formattedProduct = getFormattedItem({
        product,
        currency,
        couponNumber,
        main,
        isCrossSell,
      });

      if (product?.isUpsell && !isCrossSell) {
        saveActions(product, 'upsell', main);
      }

      trackEvent(tagName, {
        storeName,
        kioskID: kioskId,
        currency,
        value: product.price,
        coupon: couponNumber,
        items: [{ ...formattedProduct }],
      });
    } else {
      const formattedProduct = formatProduct(product, main);

      trackEvent(tagName, {
        ...formattedProduct,
        quantity: 1,
      });
    }
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error adding upsell to cart error: ${error.message}`);
  }
};

const setOrderViewed = async (items, settings, total, couponNumber, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Order Viewed event`);
    const kioskSettings = getKioskSettings(settings);
    const formattedItems = items?.map((item, idx) =>
      getFormattedItem({
        product: item,
        currency: kioskSettings.currency,
        quantity: item.quantity,
        couponNumber,
        idx,
        itemCategory: item.parentCategory,
      }),
    );

    trackEvent(tagName, {
      ...kioskSettings,
      currency: kioskSettings?.currency,
      value: total,
      item_list_name: '',
      item_list_id: '',
      items: [...formattedItems],
    });
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Order Viewed event: ${error.message}`);
  }
};

const setOrderUpdated = async tagName => {
  trackEvent(tagName);
};

const setOrderCompleted = async tagName => {
  trackEvent(tagName);
};

const setPaymentStarted = async (paymentType, tagName) => {
  logger.info(`${JITSU_LOG_TAG} - Sending Payment Started event`);
  trackEvent(tagName, { paymentType });
};

const setUpsellDismissed = async (products, tagName) => {
  trackEvent(tagName, { products });
};

const setUpsizeItemSelected = (product, settings, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Upsize Selected event`);
    const kioskSettings = getKioskSettings(settings);
    const { name, parentCategory } = product;

    saveActions(product, 'upsize');

    trackEvent(tagName, {
      ...kioskSettings,
      upsizeProduct: name?.text || name,
      size: 'large',
      upsizeSelectedFrom: parentCategory,
    });
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Upsize selected event: ${error.message}`);
  }
};

const setPaymentCompleted = async (order, cart, couponNumber, addedUpsells, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Payment Completed event`);

    if (process.env.POI_APP_IS_NEW_GA4_ENABLED) {
      const paymentType = order?.paymentType || order.payment?.paymentType;
      const {
        orderNumber,
        transactionId,
        settings,
        session,
        financialSummary,
        cart: orderCartDetails,
      } = order || {};
      const orderMode = orderCartDetails?.mode;
      const { timestamp, sessionId } = session || {};
      const { serial, site: { name: storeName } = {}, currency } = settings?.kiosk || {};
      const { taxes, total: totalBill } = financialSummary || {};
      const { orderTime, orderDay } = getOrderTime(timestamp);

      const cartItems = cart?.map((item, idx) => {
        const mainProduct = addedUpsells?.find(
          upsell => upsell.productId === (item.productId || item.id),
        );
        const formattedItem = getFormattedItem({
          product: item,
          currency,
          quantity: item.count?.current || item.quantity || 1,
          idx,
          itemCategory: item.parentCategory,
          main: mainProduct?.main,
          isCrossSell: item.isCrossSell,
        });
        if (formattedItem) {
          const itemIdForKey =
            formattedItem.item_id || item?.productId || item?.id || `unknown-${idx}`;
          formattedItem.cartKey = `${itemIdForKey}-${idx}`;
          formattedItem.quantity = Number(formattedItem.quantity || 1);
        }
        return formattedItem;
      });

      const extraCartItems = [];
      const consumedActionIndicesById = {};

      cartItems.forEach(cartItem => {
        const itemId = cartItem.item_id;

        while (itemId && itemMarketingData[itemId] && cartItem.quantity > 0) {
          const actionIndexToUse = consumedActionIndicesById[itemId] || 0;
          const actionsForId = itemMarketingData[itemId];

          if (actionsForId && actionIndexToUse < actionsForId.length) {
            const marketingData = actionsForId[actionIndexToUse];
            let actionConsumedThisIteration = false;
            let newMarketingCartItem;

            try {
              newMarketingCartItem = JSON.parse(JSON.stringify(cartItem));
            } catch (e) {
              logger.error(`${JITSU_LOG_TAG} - Error cloning cartItem for marketing: ${e.message}`);
              break;
            }

            if (marketingData.action === 'cross-sell') {
              newMarketingCartItem.quantity = 1;
              newMarketingCartItem.original_item = '';
              newMarketingCartItem.marketing = {
                action_page_path: marketingData.action_page_path,
                action: marketingData.action,
              };
              delete newMarketingCartItem.internal_upsize_action_data;
              extraCartItems.push(newMarketingCartItem);
              /* eslint no-param-reassign: "error" */
              cartItem.quantity -= 1;
              actionConsumedThisIteration = true;
            } else if (marketingData.action === 'upsell') {
              newMarketingCartItem.quantity = 1;
              newMarketingCartItem.original_item = marketingData.main_item_name || '';
              newMarketingCartItem.marketing = {
                action_page_path: marketingData.action_page_path,
                action: marketingData.action,
              };
              delete newMarketingCartItem.internal_upsize_action_data;
              extraCartItems.push(newMarketingCartItem);
              cartItem.quantity -= 1;
              actionConsumedThisIteration = true;
            } else if (marketingData.action === 'upsize') {
              const itemPrice = Number(cartItem.price);
              const actionPrice =
                marketingData.actionPrice !== null && marketingData.actionPrice !== undefined
                  ? Number(marketingData.actionPrice)
                  : NaN;

              if (
                !Number.isNaN(itemPrice) &&
                !Number.isNaN(actionPrice) &&
                itemPrice === actionPrice
              ) {
                cartItem.internal_upsize_action_data = marketingData;
                actionConsumedThisIteration = true;
              } else {
                if (
                  !Number.isNaN(itemPrice) &&
                  !Number.isNaN(actionPrice) &&
                  itemPrice !== actionPrice
                ) {
                  logger.warn(
                    `${JITSU_LOG_TAG} - Upsize price mismatch for ${itemId}. Item: ${itemPrice}, Action: ${actionPrice}`,
                  );
                }
                actionConsumedThisIteration = true;
              }
            } else {
              logger.warn(
                `${JITSU_LOG_TAG} - Unknown marketing action type processed: ${marketingData.action} for item ${itemId}`,
              );
              actionConsumedThisIteration = true;
            }

            if (actionConsumedThisIteration) {
              consumedActionIndicesById[itemId] = actionIndexToUse + 1;
            } else {
              // If action wasn't consumed (e.g., bad type, failed match), break to avoid infinite loop.
              // Although current logic consumes all paths, this is a safeguard.
              break;
            }

            if (cartItem.quantity <= 0) {
              break;
            }
          } else {
            break;
          }
        }
      });

      const finalFilteredCartItems = [];
      const itemsToRemove = new Set();

      cartItems.forEach(item => {
        if (item.quantity <= 0) {
          itemsToRemove.add(item.cartKey);
        } else {
          const standardOrUpsizedItem = { ...item };
          delete standardOrUpsizedItem.marketing;
          standardOrUpsizedItem.original_item = '';
          delete standardOrUpsizedItem.addon;

          if (standardOrUpsizedItem.internal_upsize_action_data) {
            standardOrUpsizedItem.marketing = {
              action_page_path: standardOrUpsizedItem.internal_upsize_action_data.action_page_path,
              action: standardOrUpsizedItem.internal_upsize_action_data.action,
            };
          }
          delete standardOrUpsizedItem.internal_upsize_action_data;
          finalFilteredCartItems.push(standardOrUpsizedItem);
        }
      });

      const combinedCartItems = [...finalFilteredCartItems, ...extraCartItems];

      const aggregatedItemsMap = new Map();
      combinedCartItems.forEach(item => {
        let key;
        const marketingAction = item.marketing?.action;
        const priceStr = String(item.price ?? ''); // Use consistent price string

        if (marketingAction === 'upsell') {
          // Group upsells by item ID AND the original item they relate to
          key = `${item.item_id}|${item.original_item}|upsell`;
        } else if (marketingAction === 'cross-sell') {
          // Group cross-sells just by item ID and price (no original_item linking)
          key = `${item.item_id}|${priceStr}|cross-sell`;
        } else if (marketingAction === 'upsize') {
          // Group upsized items by item ID and price (to distinguish from standard)
          key = `${item.item_id}|${priceStr}|upsize`;
        } else {
          // Standard items (no marketing action): group by item ID and price
          key = `${item.item_id}|${priceStr}|standard`;
        }

        if (aggregatedItemsMap.has(key)) {
          const existingItem = aggregatedItemsMap.get(key);
          existingItem.quantity += item.quantity;
        } else {
          const { cartKey, internal_upsize_action_data, ...itemCopy } = item;
          aggregatedItemsMap.set(key, itemCopy);
        }
      });
      const aggregatedItems = Array.from(aggregatedItemsMap.values());

      // Sort and assign final indices using the aggregated list
      const sortedCartItems = sortAndHandleDuplicates(aggregatedItems);
      const finalCartItems = sortedCartItems.map(item => {
        const { cartKey, ...rest } = item;
        return rest;
      });

      trackEvent(tagName, {
        storeName,
        kioskID: serial,
        payment_type: paymentType,
        orderTime,
        disposition: orderMode,
        purchase_day: orderDay,
        transaction_id: sessionId || transactionId || orderNumber,
        tax: taxes,
        shipping: 0,
        coupon: couponNumber,
        value: totalBill || null,
        currency,
        items: finalCartItems,
      });
      resetActions();
    } else {
      const products = formatProductsCart(cart, addedUpsells);
      const items = cart.length;
      const total = order.cart?.total || null;
      const paymentType = order?.paymentType || order.payment?.paymentType;
      const cardType = order.payment?.cardType;
      const { orderNumber, transactionId } = order;

      trackEvent(tagName, {
        items,
        products,
        revenue: total,
        paymentType,
        cardType,
        orderNumber,
        transactionId,
      });
      resetActions();
    }
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Payment Completed event: ${error.message}`);
    resetActions();
  }
};

const setProductViewed = async (
  id,
  product,
  currency,
  settings,
  couponNumber,
  itemCategory,
  tagName,
) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Product Viewed event`);

    if (process.env.POI_APP_IS_NEW_GA4_ENABLED) {
      const kioskSettings = getKioskSettings(settings);

      const formattedProduct = getFormattedItem({
        product,
        currency,
        quantity: 1,
        couponNumber,
        itemCategory,
      });

      trackEvent(tagName, {
        ...kioskSettings,
        value: product.price,
        items: [
          {
            ...formattedProduct,
            item_id: id,
            item_name: product?.name
              ? product?.name?.text
              : product?.grouped
              ? product?.grouped
                  .map(item => `${item?.name?.text}  | `)
                  .join(' ')
                  .slice(0, -3)
              : product?.options
              ? product?.options
                  .map(item => `${item?.name?.text}  | `)
                  .join(' ')
                  .slice(0, -3)
              : '',
          },
        ],
      });
    } else {
      trackEvent(tagName, {
        product_id: id,
        product_name: product?.name
          ? product?.name?.text
          : product?.grouped
          ? product?.grouped
              .map(item => `${item?.name?.text}  | `)
              .join(' ')
              .slice(0, -3)
          : product?.options
          ? product?.options
              .map(item => `${item?.name?.text}  | `)
              .join(' ')
              .slice(0, -3)
          : '',
        product_type: product?.type || product?.dataTag?.split('-').shift() || '',
        price: product?.price,
        currency,
      });
    }
  } catch (error) {
    logger.error(`${JITSU_LOG_TAG} - Error Product Viewed event: ${error.message}`);
  }
};

const setLoyaltyLoginSuccessTrack = data => {
  trackEvent('Loyalty login success', data);
};

const setLoyaltyLogoutSuccessTrack = async () => {
  trackEvent('Loyalty logout success', {});
};

const setLoyaltyLoginFailTrack = async () => {
  trackEvent('Loyalty login fail', {});
};

const setLoyaltyClaimTrack = async data => {
  trackEvent('Loyalty claim', data);
};

const setLoyaltyPossibleRedemptionsTrack = async data => {
  trackEvent('Loyalty Possible Redemptions', data);
};

const setLoyaltyCreateRedemptionsTrack = async data => {
  trackEvent('Loyalty Create Redemptions', data);
};

const setLoyaltyGuestClaimTrack = async data => {
  trackEvent('Loyalty guest claim', data);
};

const setBeginCheckoutTrack = async (
  items,
  settings,
  cartTotal,
  couponNumber,
  orderMode,
  upsellDependency,
  tagName,
) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Begin Checkout event`);
    const kioskSettings = getKioskSettings(settings);

    const formattedItems = items?.map((item, idx) => {
      const mainProduct = upsellDependency?.find(upsell => upsell.productId === item.id);
      return getFormattedItem({
        product: item?.product,
        currency: kioskSettings.currency,
        quantity: item.quantity,
        couponNumber,
        idx,
        itemCategory: item.parentCategory,
        main: mainProduct?.main,
      });
    });

    trackEvent(tagName, {
      ...kioskSettings,
      disposition: orderMode,
      coupon: couponNumber,
      value: cartTotal,
      currency: kioskSettings.currency,
      items: [...(formattedItems || [])],
    });
  } catch (error) {
    logger.error(
      `${JITSU_LOG_TAG} - Error while sending Begin checkout event error: ${error.message}`,
    );
  }
};

const setViewItemListTrack = async ({ deviceSettings, products, overrides }, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending View Item List event`);

    const formattedDeviceSettings = getKioskSettings(deviceSettings);
    const { item_list_name = '', item_id = '' } = overrides ?? {};

    const items = products?.map((menuProduct, index) => {
      const menuProductOverrides = {
        item_list_name,
        item_list_id: item_id,
        index: index + 1,
      };
      return formatMenuProductToAnalyticsProduct({ menuProduct, overrides: menuProductOverrides });
    });

    const eventData = {
      ...formattedDeviceSettings,
      item_list_name,
      item_id,
      items,
    };

    trackEvent(tagName, eventData);
  } catch (error) {
    logger.error(
      `${JITSU_LOG_TAG} - Error while sending View Item List event error: ${error.message}`,
    );
  }
};

const setCartItemEdit = async (settings, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Cart item edit event`);
    const kioskSettings = getKioskSettings(settings);

    trackEvent(tagName, { ...kioskSettings });
  } catch (error) {
    logger.error(
      `${JITSU_LOG_TAG} - Error while sending Cart item edit event error: ${error.message}`,
    );
  }
};

const setItemSelectTrack = async (
  product,
  cartItems,
  settings,
  couponNumber,
  itemCategory,
  tagName,
) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Select Item event`);
    const kioskSettings = getKioskSettings(settings);

    const productDetails = {
      quantity: 1,
      product,
      parentCategory: itemCategory,
    };
    let formattedItems = [productDetails, ...cartItems];

    formattedItems = formattedItems?.map((item, idx) =>
      getFormattedItem({
        product: item.product,
        currency: kioskSettings.currency,
        quantity: item.quantity,
        couponNumber,
        idx,
        itemCategory: product.parentCategory,
      }),
    );

    trackEvent(tagName, {
      ...kioskSettings,
      item_list_name: '', // TODO
      item_list_id: '', // TODO
      items: formattedItems,
    });
  } catch (error) {
    logger.error(
      `${JITSU_LOG_TAG} - Error while sending Item select event error: ${error.message}`,
    );
  }
};

const setEmbeddedErrorTrack = async (settings, errorMessage, pageTitle, tagName) => {
  try {
    logger.info(`${JITSU_LOG_TAG} - Sending Show Embedded Error event`);
    const kioskSettings = getKioskSettings(settings);

    trackEvent(tagName, {
      ...kioskSettings,
      popupOrPageTitle: pageTitle,
      errorMessage,
    });
  } catch (error) {
    logger.error(
      `${JITSU_LOG_TAG} - Error while Show embedded error event error: ${error.message}`,
    );
  }
};

export const useSetTrack = () => {
  return {
    setDineMode,
    setProductAddedToCart,
    setProductRemoved,
    setUpsellAddedToCart,
    setOrderUpdated,
    setOrderCompleted,
    setPaymentStarted,
    setOrderViewed,
    setPaymentCompleted,
    setProductViewed,
    setUpsellDismissed,
    setPage,
    setBeginCheckoutTrack,
    setViewItemListTrack,
  };
};

export {
  setDineMode,
  setProductAddedToCart,
  setProductRemoved,
  setUpsellAddedToCart,
  setOrderUpdated,
  setOrderCompleted,
  setPaymentStarted,
  setOrderViewed,
  setPaymentCompleted,
  setProductViewed,
  setUpsellDismissed,
  setPage,
  setLoyaltyLoginSuccessTrack,
  setLoyaltyLogoutSuccessTrack,
  setLoyaltyLoginFailTrack,
  setLoyaltyClaimTrack,
  setLoyaltyPossibleRedemptionsTrack,
  setLoyaltyCreateRedemptionsTrack,
  setLoyaltyGuestClaimTrack,
  setBeginCheckoutTrack,
  setUpsizeItemSelected,
  setViewItemListTrack,
  setCartItemEdit,
  setItemSelectTrack,
  setEmbeddedErrorTrack,
};
